import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Brain, 
  Loader2, 
  AlertTriangle, 
  Shield, 
  Heart, 
  Target,
  Sparkles,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIAnalysis() {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisType, setAnalysisType] = useState('geral');
  const [context, setContext] = useState('');
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [previousAnalyses, setPreviousAnalyses] = useState([]);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);

  useEffect(() => {
    fetchPreviousAnalyses();
  }, []);

  const fetchPreviousAnalyses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/analyses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreviousAnalyses(data);
      }
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setCurrentAnalysis(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/analyze-risks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis_type: analysisType,
          context: context || null,
          include_employees: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentAnalysis(data);
        fetchPreviousAnalyses();
        toast.success('Análise concluída com sucesso!');
      } else {
        const error = await response.json();
        toast.error(`Erro: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      toast.error('Erro ao executar análise');
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadPDF = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai/analyses/${analysisId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analise_ia_${analysisId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF gerado com sucesso!');
      } else {
        toast.error('Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erro ao baixar PDF');
    }
  };

  const analysisTypes = [
    { id: 'geral', label: 'Análise Geral', icon: Shield, description: 'Visão completa da situação de SST' },
    { id: 'psicossocial', label: 'Riscos Psicossociais', icon: Heart, description: 'Fatores de saúde mental e clima organizacional' },
    { id: 'ocupacional', label: 'Riscos Ocupacionais', icon: AlertTriangle, description: 'Riscos físicos, químicos, biológicos e ergonômicos' },
    { id: 'prevencao', label: 'Plano de Prevenção', icon: Target, description: 'Ações e KPIs para melhoria contínua' }
  ];

  const getRiskLevelBadge = (level) => {
    const config = {
      'Baixo': { className: 'bg-green-100 text-green-800', icon: '🟢' },
      'Médio': { className: 'bg-yellow-100 text-yellow-800', icon: '🟡' },
      'Alto': { className: 'bg-orange-100 text-orange-800', icon: '🟠' },
      'Crítico': { className: 'bg-red-100 text-red-800', icon: '🔴' }
    };
    const cfg = config[level] || config['Médio'];
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${cfg.className}`}>
        {cfg.icon} {level}
      </span>
    );
  };

  const formatAnalysisText = (text) => {
    // Convert markdown-like formatting to HTML
    return text
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('###')) {
          return <h4 key={i} className="font-bold text-lg mt-4 mb-2">{line.replace(/^###\s*/, '')}</h4>;
        }
        if (line.startsWith('##')) {
          return <h3 key={i} className="font-bold text-xl mt-4 mb-2">{line.replace(/^##\s*/, '')}</h3>;
        }
        if (line.startsWith('#')) {
          return <h2 key={i} className="font-bold text-2xl mt-4 mb-2">{line.replace(/^#\s*/, '')}</h2>;
        }
        if (line.match(/^\d+\./)) {
          return <p key={i} className="ml-4 mb-1"><strong>{line.split('.')[0]}.</strong>{line.substring(line.indexOf('.') + 1)}</p>;
        }
        if (line.startsWith('-') || line.startsWith('•') || line.startsWith('*')) {
          return <p key={i} className="ml-6 mb-1">• {line.replace(/^[-•*]\s*/, '')}</p>;
        }
        if (line.trim() === '') {
          return <br key={i} />;
        }
        return <p key={i} className="mb-2">{line}</p>;
      });
  };

  return (
    <div className="space-y-6" data-testid="ai-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            Análise Inteligente de Riscos
          </h1>
          <p className="text-gray-500">Use IA para analisar dados e receber recomendações personalizadas</p>
        </div>
      </div>

      {/* Analysis Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {analysisTypes.map((type) => {
          const Icon = type.icon;
          const isSelected = analysisType === type.id;
          
          return (
            <Card 
              key={type.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : ''
              }`}
              onClick={() => setAnalysisType(type.id)}
              data-testid={`analysis-type-${type.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-purple-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium">{type.label}</p>
                    <p className="text-sm text-gray-500">{type.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Context Input */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contexto Adicional (Opcional)</CardTitle>
          <CardDescription>
            Forneça informações específicas que a IA deve considerar na análise
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Ex: Tivemos um aumento de acidentes no setor de produção nos últimos 3 meses..."
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            data-testid="analysis-context-input"
          />
          <Button 
            onClick={runAnalysis} 
            disabled={analyzing}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="run-analysis-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analisando dados...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Executar Análise com IA
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Current Analysis Result */}
      {currentAnalysis && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Resultado da Análise
              </CardTitle>
              <div className="flex items-center gap-3">
                {getRiskLevelBadge(currentAnalysis.risk_level)}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadPDF(currentAnalysis.id)}
                  className="flex items-center gap-2"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4" />
                  Gerar PDF
                </Button>
              </div>
            </div>
            <CardDescription>
              {analysisTypes.find(t => t.id === currentAnalysis.analysis_type)?.label} • 
              Gerado em {new Date(currentAnalysis.generated_at).toLocaleString('pt-BR')} •
              Modelo: {currentAnalysis.model_used || 'gemini-2.5-flash'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Analysis Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-lg border text-center">
                <p className="text-2xl font-bold text-purple-600">{currentAnalysis.risks_analyzed}</p>
                <p className="text-xs text-gray-500">Riscos Analisados</p>
              </div>
              <div className="bg-white p-3 rounded-lg border text-center">
                <p className="text-2xl font-bold text-purple-600">{currentAnalysis.employees_count}</p>
                <p className="text-xs text-gray-500">Funcionários</p>
              </div>
              <div className="bg-white p-3 rounded-lg border text-center">
                <p className="text-2xl font-bold text-purple-600">{currentAnalysis.recommendations?.length || 0}</p>
                <p className="text-xs text-gray-500">Recomendações</p>
              </div>
            </div>
            
            {/* Analysis Text */}
            <div className="prose prose-sm max-w-none bg-white p-4 rounded-lg border overflow-auto max-h-[600px]" data-testid="analysis-result">
              {formatAnalysisText(currentAnalysis.analysis)}
            </div>
            
            {/* Recommendations */}
            {currentAnalysis.recommendations?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-600" />
                  Recomendações Principais:
                </h4>
                <ul className="space-y-2">
                  {currentAnalysis.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 bg-purple-50 p-2 rounded-lg">
                      <span className="text-purple-600 font-bold mt-0.5">{i + 1}.</span>
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previous Analyses */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Análises Anteriores
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchPreviousAnalyses}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {previousAnalyses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma análise realizada ainda</p>
              <p className="text-sm">Execute sua primeira análise acima</p>
            </div>
          ) : (
            <div className="space-y-3">
              {previousAnalyses.map((analysis) => (
                <div key={analysis.id} className="border rounded-lg p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedAnalysis(expandedAnalysis === analysis.id ? null : analysis.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Brain className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {analysisTypes.find(t => t.id === analysis.analysis_type)?.label || analysis.analysis_type}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(analysis.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getRiskLevelBadge(analysis.risk_level)}
                      {expandedAnalysis === analysis.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedAnalysis === analysis.id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg overflow-auto max-h-[400px]">
                        {formatAnalysisText(analysis.analysis)}
                      </div>
                      {analysis.recommendations?.length > 0 && (
                        <div className="mt-4">
                          <h5 className="font-medium text-gray-700 mb-2">Recomendações:</h5>
                          <ul className="text-sm space-y-1">
                            {analysis.recommendations.slice(0, 5).map((rec, i) => (
                              <li key={i} className="text-gray-600">• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-4 flex justify-end">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPDF(analysis.id);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Baixar PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
